import {
    AssetTypeEnum,
    AssetTypeLabels,
    SiteReccePriorityEnum,
    SiteReccePriorityLabels,
    SiteRecceStatusEnum,
    SiteRecceStatusLabels,
    SiteRecceTypeEnum,
    SiteRecceTypeLabels,
} from '@api/enum';
import { ISiteReccee } from '@api/models/site-recce.model';
import FormikDatePicker from '@components/FormFields/FormikDatePicker';
import useDialog from '@hooks/use-dialog';
import { Button, Card, Chip, HelpText, Label } from '@hyperflake/react-ui-library';
import { LockIcon, UnlockIcon } from '@icon/index';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import useRemarksUpdateSiteRecce from 'modules/site-recce/hooks/useRemarksUpdateSiteRecce';
import useSiteRecceUpdateForm from 'modules/site-recce/hooks/useSiteRecceUpdateForm';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DeleteSiteRecceDrawer from '../DeleteSiteRecceDrawer/DeleteSiteRecceDrawer';
import FileUploadSiteRecce from '../FileUploadSiteRecce/FileUploadSiteRecce';
import SiteRecceRemarksTimeline from '../SiteRecceRemarksTimeline/SiteRecceRemarksTimeline';

interface UpdateSiteRecceFormProps {
    clientInstallationId: string;
    projectInstallationId: string;
    initialData: ISiteReccee;
    setData: (data: any) => void;
}

const UpdateSiteRecceForm = (props: UpdateSiteRecceFormProps) => {
    const { clientInstallationId, projectInstallationId, initialData, setData } = props;
    const formHook = useSiteRecceUpdateForm(clientInstallationId, projectInstallationId, initialData);
    const navigate = useNavigate();
    const deleteSiteRecceDailog = useDialog<ISiteReccee>();
    const [enableEdit, setEnableEdit] = useState<boolean>(false);
    const { cancelRecce, completeRecce, addRemarks, removeRemark, isCancelling, isCompleting, isAdding } =
        useRemarksUpdateSiteRecce(clientInstallationId, projectInstallationId, initialData._id);

    const handleAddRemarks = async (remarks: string) => {
        try {
            const data = await addRemarks(remarks);
            toast.success('Remark Added Successfully');
            // setEnableEdit(false);
            // await queryClient.invalidateQueries(['site-recce-counter']);
            setData(data);
            // navigate(`/all-site-recce?status=${data.status}`);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };
    const handleRemoveRemark = async (remarkId: string) => {
        try {
            // Call your delete API here — this is just an example:
            const data = await removeRemark(remarkId);
            toast.success('Remark removed');
            setData(data);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to remove remark');
        }
    };

    const priorityOptions = useMemo(
        () =>
            Object.values(SiteReccePriorityEnum).map((priority) => ({
                label: SiteReccePriorityLabels[priority], // Use human-readable label
                value: priority,
            })),
        []
    );

    const typeOptions = useMemo(() => {
        return [
            { label: 'Select Type', value: '' },
            ...Object.values(SiteRecceTypeEnum).map((val) => ({
                label: SiteRecceTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    const assetTypeOptions = useMemo(() => {
        return [
            { label: 'Select Asset', value: '' },
            ...Object.values(AssetTypeEnum).map((val) => ({
                label: AssetTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    const handleSubmit = async (values: any) => {
        try {
            await formHook.submit(values);
            toast.success('Recce Updated Successfully');
            navigate(
                `/client-installations/${clientInstallationId}/project-installations/${projectInstallationId}/recce`
            );
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };

    return (
        <>
            <Formik
                initialValues={formHook.initialValues}
                enableReinitialize
                validationSchema={formHook.schema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <Card className="mt-4">
                            <Card.Header className="flex justify-between items-center">
                                <div>Basic Information</div>
                                <div>
                                    {enableEdit ? (
                                        <div className="flex gap-4">
                                            <Button type="button" variant="shaded" onClick={() => setEnableEdit(false)}>
                                                Cancel
                                            </Button>
                                            <Button disabled={formHook.isSubmitting || !enableEdit}>
                                                {formHook.isSubmitting ? 'Updating...' : 'Save'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button type="button" variant="shaded" onClick={() => setEnableEdit(true)}>
                                            {!enableEdit ? (
                                                <LockIcon width={18} height={22} className="text-grayscale-600" />
                                            ) : (
                                                <UnlockIcon width={18} height={22} className="text-grayscale-600" />
                                            )}
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <fieldset disabled={!enableEdit}>
                                    {/* Type */}

                                    <div className="flex items-center gap-4">
                                        <div className="text-gray-400 text-sm">Status</div>
                                        <Chip
                                            size="lg"
                                            label={SiteRecceStatusLabels[initialData.status]}
                                            color={
                                                initialData.status === SiteRecceStatusEnum.UNASSIGNED
                                                    ? 'secondary'
                                                    : initialData.status === SiteRecceStatusEnum.ASSIGNED
                                                    ? 'warning'
                                                    : initialData.status === SiteRecceStatusEnum.COMPLETED
                                                    ? 'success'
                                                    : 'danger'
                                                // : 'success'
                                            }
                                            variant="default"
                                        />
                                    </div>
                                    <div className="mt-4">
                                        <Label>Type</Label>
                                        <FormikSelect name="type" options={typeOptions} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="type" />
                                        </HelpText>
                                    </div>

                                    {/* Asset Type */}
                                    <div className="mt-4">
                                        <Label>Asset Type</Label>
                                        <FormikSelect name="assetType" options={assetTypeOptions} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="assetType" />
                                        </HelpText>
                                    </div>
                                    <div className="mt-4">
                                        <Label>Quantity</Label>
                                        <FormikInput name="quantity" type="number" />
                                        <HelpText variant="error">
                                            <ErrorMessage name="quantity" />
                                        </HelpText>
                                    </div>

                                    {/* Priority */}
                                    <div className="mt-4">
                                        <Label>Priority</Label>
                                        <FormikSelect name="priority" options={priorityOptions} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="priority" />
                                        </HelpText>
                                    </div>

                                    {/* Recce Date */}
                                    <div className="mt-4">
                                        <Label>Preferred Date</Label>
                                        <FormikDatePicker name="preferredDate" isClearable />
                                        <HelpText variant="error">
                                            <ErrorMessage name="preferredDate" />
                                        </HelpText>
                                    </div>

                                    {/* PO Received */}
                                    <div className="mt-4 flex items-center gap-2">
                                        <Label htmlFor="poRecieved" className="mb-0">
                                            PO Received
                                        </Label>
                                        <FormikSwitch name="poRecieved" color="success" />
                                    </div>

                                    {/* Remarks */}
                                    {/* <div className="mt-4">
                                        <Label>Remarks</Label>
                                        <FormikTextarea name="remarks" rows={4} disabled={!enableEdit} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="remarks" />
                                        </HelpText>
                                    </div> */}
                                </fieldset>
                            </Card.Body>
                        </Card>

                        <Button type="submit" className="mt-6  w-full sm:w-auto" disabled={isSubmitting || !enableEdit}>
                            {isSubmitting ? 'Updating...' : 'Update Recce'}
                        </Button>
                    </Form>
                )}
            </Formik>

            <FileUploadSiteRecce
                clientInstallationId={clientInstallationId}
                projectInstallationId={projectInstallationId}
                setData={setData}
                initialData={initialData}
                enableEdit={enableEdit}
            />
            {enableEdit && (
                <Card className="mt-4">
                    <Card.Header>Delete Recce</Card.Header>
                    <Card.Body>
                        <Button onClick={() => deleteSiteRecceDailog.show(initialData)}>Delete</Button>
                    </Card.Body>
                </Card>
            )}

            <SiteRecceRemarksTimeline
                handleAddRemarks={handleAddRemarks}
                handleRemoveRemark={handleRemoveRemark}
                data={initialData.remarks}
            />

            <DeleteSiteRecceDrawer
                clientInstallationId={clientInstallationId}
                projectInstallationId={projectInstallationId}
                onClose={deleteSiteRecceDailog.hide}
                open={deleteSiteRecceDailog.isOpen}
                siteRecce={deleteSiteRecceDailog.data}
            />
        </>
    );
};

export default UpdateSiteRecceForm;
